'use client';

/**
 * CourseRow (T10d) — compact single-line list row, the "many results" height
 * of CoursePanel's "one list, two heights" design. Deliberately NOT a link
 * itself — RegisterCTA is the only actionable target in the row, avoiding
 * the classic a11y trap of a link wrapping another link/button.
 */

import React, { memo } from 'react';
import type { Course } from '@/types/course';
import { cn } from '@/lib/utils';
import { courseMode } from '@/lib/course-grouping';
import {
  DateTile,
  DATE_TILE_MODE,
} from '@/components/assistant/primitives/date-tile';
import { RegisterCTA } from '@/components/assistant/primitives/register-cta';
import { fmtWeekdaySpan } from '@/lib/course-date-format';

export interface CourseRowProps {
  course: Course;
  /** Prepend the program name to the bold line ("Part 2 · Cupertino, CA").
   *  CoursePanel sets this when its list holds MIXED titles (a title-less
   *  multi-location search, FB33) — the row normally carries no title (the
   *  uniform-title panel header names the program once), which would leave
   *  mixed rows indistinguishable. Kept inside the bold line: the T10d row
   *  is a strict two-line design. */
  showProgram?: boolean;
  className?: string;
}

export const CourseRow = memo(function CourseRow({
  course,
  showProgram = false,
  className,
}: CourseRowProps) {
  const {
    city,
    locationCity,
    state,
    eventStartDate,
    eventEndDate,
    eventTimeZone,
    distance,
    url,
    unitPrice,
    mode,
  } = course;

  const isOnline = courseMode(course) === 'online';
  const displayCity = city || locationCity;

  const placeLine = isOnline
    ? `Online · ${eventTimeZone || 'Live on Zoom'}`
    : displayCity
      ? `${displayCity}${state ? `, ${state}` : ''}`
      : mode;

  // Same brand-prefix strip as the panel header — row space is tight.
  const programName = (course.title ?? '').replace(/^Art of Living\s+/i, '');
  const locationLine =
    showProgram && programName ? `${programName} · ${placeLine}` : placeLine;

  // Design shows sentence-case mode labels ("In person", not the backend's
  // "In Person") — display-only transform, never sent back.
  const modeLabel = mode
    ? mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase()
    : null;

  const metaParts = [
    fmtWeekdaySpan(eventStartDate, eventEndDate),
    modeLabel,
    distance != null ? `${distance.toFixed(1)} mi` : null,
  ].filter(Boolean);

  return (
    <div
      className={cn(
        // Tighter gaps/padding below sm: the middle text block is the only
        // squeezable child and needs every px on a 390px screen (FB26).
        'flex items-center gap-[10px] rounded-[14px] border-[1px] border-[#f0e7da] bg-white px-[12px] py-[13px] sm:gap-[15px] sm:px-[15px]',
        'transition-colors hover:border-[#E8C7A0] hover:bg-[#FFFCF8]',
        className
      )}
    >
      {eventStartDate ? (
        <DateTile
          startDate={eventStartDate}
          endDate={eventEndDate || undefined}
          mode={isOnline ? DATE_TILE_MODE.ONLINE : DATE_TILE_MODE.IN_PERSON}
        />
      ) : (
        // Undated course (groupCourses' "Dates TBD" bucket) — a real DateTile
        // would render an Invalid Date month, so show a neutral placeholder.
        <div
          className="flex w-[66px] flex-col items-center justify-center rounded-xl bg-[#FBF1E2] py-2 font-['Work_Sans']"
          aria-hidden="true"
        >
          <span className="text-[11px] font-bold uppercase leading-none tracking-[.06em] text-[#B0863C]">
            TBD
          </span>
        </div>
      )}

      {/* T10d row is TWO lines: location, then one meta line with the price
          INLINE at its end ("Sat – Sun · In person · $100") — never a third
          line; that's what keeps the row at the design's ~66px content height.
          The price is a shrink-0 sibling of the truncating meta text (not
          inside it): on narrow phones the meta line gets ~90px and a single
          truncate would swallow the price — the row's most decision-relevant
          datum — first (FB26). */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold leading-tight text-[#31364e]">
          {locationLine}
        </div>
        <div className="mt-[4px] flex items-baseline text-[13px] leading-tight text-[#8a7c68]">
          <span className="truncate">
            {metaParts.join(' · ')}
            {metaParts.length > 0 && ' · '}
          </span>
          <span className="shrink-0 font-[family-name:var(--font-lora)] text-[14px] font-semibold text-[#E36001]">
            {unitPrice != null ? `$${unitPrice}` : 'Free'}
          </span>
        </div>
      </div>

      <RegisterCTA href={url || '#'} size={40}>
        Register
      </RegisterCTA>
    </div>
  );
});

export default CourseRow;
